import React, { useEffect, useMemo, useState } from 'react';
import { FunctionField, Link, ListProps, TextField, useGetList } from 'react-admin';

import {
  BooleanField,
  TextInput,
  ImageField,
  OptionsField,
  ExternalUrlField,
  ReferenceInput,
  SelectInput,
  Grid,
  GridList,
  ManyFilter,
  Forbidden,
  NullableBooleanInput,
  SelectArrayInput, OptionsAutocompleteFilter,
} from '@components';
import { AdminOptionCats } from '@types';
import { CropsField } from '../CropsField';
import { PricesField } from '../PricesField';
import { adminSettingsHttp } from '@network';
import { ImportCsv } from '@pages/product/ImportCsv';
import { useExporter, useRbacEdit, useRbacList } from '@hooks';
import { ProductListActions } from '@pages/product/ProductListActions';
import cls from './ProductList.module.css';


export const ProductList = (props: ListProps) => {
  const rbacList = useRbacList();
  const rbacEdit = useRbacEdit();
  const [forms, setForms] = useState<{id: string, name: string}[]>([]);
  const [showImport, setShowImport] = useState(false);

  const { data: crops } = useGetList('crop', {
    pagination: { page: 1, perPage: 1000 },
  });

  const cropsMap = useMemo(() => {
    const map = new Map<string, string>();

    crops?.forEach(c => {
      map.set(`${c.id}`, c.nameMx);
    });

    return map;
  }, [crops]);

  const exporter = useExporter({
    filename: 'Verqor Products',
    props: {
      'sku': {
        label: 'SKU',
      },
      'name': {
        label: 'Name',
      },
      'price': {
        label: 'Price',
        transform: 'money',
      },
      'image': {
        label: 'Image',
      },
      'brand': {
        label: 'Brand',
        prop: 'brand.name',
      },
      'division': {
        label: 'Category',
        prop: 'division.name',
      },
      'type': {
        label: 'Subcategory',
        prop: 'type.name',
      },
      'pack': {
        label: 'Pack',
      },
      'packTypes': {
        label: 'Pack Types',
      },
      'application': {
        label: 'Application',
      },
      'form': {
        label: 'Form',
      },
      'dosePerHa': {
        label: 'Dose Per Ha',
      },
      'composition': {
        label: 'Composition',
      },
      'description': {
        label: 'Description',
      },
      'pdfLink': {
        label: 'PDF Link',
      },
      'position': {
        label: 'Position',
      },
      'discount': {
        label: 'Discount',
      },
      'crops': {
        label: 'Crops',
        transform: ids => {
          return ids
            .map((id: string) => cropsMap?.get(`${id}`))
            .filter(Boolean)
            .join(', ');
        },
      },
      'isEnabled': {
        label: 'In Marketplace',
        transform: 'boolean',
      },
      'isExternal': {
        label: 'External',
        transform: 'boolean',
      },
      'externalShop': {
        label: 'External Shop',
        transform: 'boolean',
      },
      'isNew': {
        label: 'New',
        transform: 'boolean',
      },
      'origin': {
        label: 'Origin',
        transform: o => o ?
          o.toLowerCase().replace(/^./, (c) => c.toUpperCase())
          : '',
      },
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const formsList = await adminSettingsHttp
          .getOptionsByCategory(AdminOptionCats.PRODUCT_PRESENTATION);

        if (formsList?.length) {
          setForms(formsList.map(i => ({
            id: i.id ? `${i.id}` : '',
            name: i.name,
          })));
        }
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  if (!rbacList) {
    return <Forbidden />;
  }

  return (
    <>
      <GridList
        {...props}
        editComponent={rbacEdit ? <ProductListActions /> : undefined}
        filters={[
          <TextInput key="q" source="q" variant="outlined" label="Search by name" alwaysOn />,
          <TextInput key="s" source="s" label="Search by content" />,
          <OptionsAutocompleteFilter
            key="distributor"
            source="distributor"
            label="Brand"
            alwaysOn
            style={{ width: 240 }}
          />,
          <ManyFilter key="crops" source="crops" reference="crop" alwaysOn />,
          <NullableBooleanInput key="isExternal" source="isExternal" label="External" alwaysOn />,
          <ReferenceInput
            key="division"
            reference="division"
            source="divisionId"
            perPage={1000}
            sort={{ field: 'name', order: 'ASC' }}
          >
            <SelectInput optionText="name" />
          </ReferenceInput>,
          <OptionsField key="category" source="category" />,
          <OptionsField key="subcategory" source="subcategory" />,
          <OptionsField key="pack" source="pack" />,
          <OptionsField key="application" source="application" />,
          <SelectArrayInput
            key="formIds"
            source="formIds"
            choices={forms}
            className="MS-field"
            style={{ width: 80 }}
          />,
          <NullableBooleanInput key="isEnabled" label="In Marketplace" source="isEnabled" />,
          <TextInput key="dosePerHa" source="dosePerHa" />,
          <TextInput key="externalShop" source="externalShop" />,
        ]}
        sort={{ field: 'id', order: 'DESC' }}
        filterDefaultValues={{ isExternal: false }}
        mergeButton
        importButton={() => setShowImport(!showImport)}
        className={cls.table}
        exporter={exporter}
      >
        <Grid
          rowClick="edit"
          defaultColumns={'sku name price image brandId divisionId typeId crops isEnabled isExternal'.split(' ')}
        >
          <TextField source="sku" label="SKU" sortBy="position" />
          <TextField source="name" label="Name" />
          <PricesField source='price' />
          <ImageField source="image" label="Image" />
          <FunctionField
            label="Brand"
            resource="brand"
            source="brand"
            sortBy="brand"
            className={cls.link}
            render={(record: any) => record.brand ? (
              <Link
                to={`/product-brand/${record.brand?.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <TextField source="brand.name" />
              </Link>
            ) : ''} />
          <FunctionField
            label="Category"
            resource="division"
            source="division"
            sortBy="division"
            className={cls.link}
            render={(record: any) => record.division ? (
              <Link
                to={`/division/${record.division?.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <TextField source="division.name" />
              </Link>
            ) : ''} />
          <FunctionField
            label="Subcategory"
            resource="type"
            source="type"
            sortBy="type"
            className={cls.link}
            render={(record: any) => record.type ? (
              <Link
                to={`/product-type/${record.type?.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <TextField source="type.name" />
              </Link>
            ) : ''} />
          <TextField source="pack" label="Pack" />
          <TextField source="packTypes" label="Pack Types" sortable={false} />
          <TextField source="application" label="Application" />
          <TextField source="form" label="Form" />
          <TextField source="dosePerHa" label="Dose Per Ha" />
          <TextField source="composition" label="Composition" />
          <TextField source="description" label="Description" />
          <ExternalUrlField source="pdfLink" label="PDF Link" />
          <CropsField source='crops' cropsMap={cropsMap} />
          <TextField source="discount" label="Discount" />
          <BooleanField source="isEnabled" label="In Marketplace" />
          <BooleanField source="isExternal" label="For External Marketplace" />
          <TextField source="externalShop" label="External Shop" />
          <BooleanField source="isNew" label="New" />
          <FunctionField
            label="Origin"
            resource="origin"
            source="origin"
            sortBy="origin"
            render={(record: any) => record.origin ?
              record.origin.toLowerCase().replace(/^./, (c) => c.toUpperCase())
              : ''} />
        </Grid>
      </GridList>
      
      <ImportCsv open={showImport} setOpen={setShowImport}/>
    </>
  );
};

